import handsHolding from '../assets/illustrations/hands-holding.webp'
import skyFriends from '../assets/illustrations/sky-friends.webp'
import theWorld from '../assets/illustrations/the-world.webp'
import impressed from '../assets/illustrations/impressed.webp'

/**
 * Real product mockups for the how-it-works screens.
 *
 * Resolved lazily with import.meta.glob rather than a static import: these are
 * design exports that land in the repo separately, and a missing file must
 * degrade to "no image" rather than break the build. Drop files into
 * `src/assets/app/` named exactly as below and they appear automatically.
 *
 * Filenames must use underscores, never spaces — spaces in asset filenames have
 * already broken image resolution in this repo once.
 */
const appShotModules = import.meta.glob<{ default: string }>('../assets/app/*.{png,webp,jpg}', {
  eager: true,
})

function appShot(name: string): string | undefined {
  const key = Object.keys(appShotModules).find((path) => path.endsWith(`/${name}`))
  return key ? appShotModules[key].default : undefined
}

/**
 * Art slots referenced by `ArtRef` on flow nodes, resolved to real assets here
 * so the flow graph stays free of import paths.
 */
export const ART: Record<string, string | undefined> = {
  landing: impressed,
  'hands-holding': handsHolding,
  'sky-friends': skyFriends,
  'the-world': theWorld,
  impressed,
  // Supplied mockups already include the iPhone bezel, so these render bare —
  // wrapping them in our own phone frame would double-frame them.
  'app-home': appShot('home-session.png') ?? handsHolding,
  'app-experiences': appShot('experiences.png') ?? theWorld,
  'app-avatar': appShot('avatar.png'),
}

/**
 * Refs that are meant to be full device mockups. They render as a tall, bare
 * image with a drop shadow — no border, no bezel of ours, because the export
 * already includes the iPhone bezel.
 */
const DEVICE_SHOT_REFS = new Set(['app-home', 'app-experiences', 'app-avatar'])

/** Which of those refs actually resolved to a real file in `assets/app/`. */
const RESOLVED_DEVICE_SHOTS = new Set(
  [...DEVICE_SHOT_REFS].filter((ref) => {
    const file = { 'app-home': 'home-session.png', 'app-experiences': 'experiences.png', 'app-avatar': 'avatar.png' }[ref]
    return !!file && !!appShot(file)
  })
)

export function artFor(ref: string | undefined): string | undefined {
  return ref ? ART[ref] : undefined
}

/**
 * True only when the ref resolved to a genuine device mockup. A ref that fell
 * back to a brand illustration reports false, so it gets the DS illustration
 * frame instead of being rendered unframed as if it were a screenshot.
 */
export function isDeviceShot(ref: string | undefined): boolean {
  return !!ref && RESOLVED_DEVICE_SHOTS.has(ref)
}

/**
 * Emoji shown alongside each answer option. Keeps the copy files free of
 * decoration while making a wall of choices feel lighter. Purely decorative —
 * every option still carries a real text label.
 */
export const ANSWER_EMOJI: Record<string, string> = {
  // connection_context
  partner: '❤️', date: '💘', friend: '🫂', new_person: '👋', unsure: '🤷',
  // age_band
  '18_24': '🌱', '25_34': '🌿', '35_44': '🌳', '45_54': '🌻', '55_64': '🌾', '65_plus': '🌼', under_18: '🚫',
  // together_length / dating_stage / friend_length
  under_1y: '🌱', '1_3y': '🌿', '3_7y': '🌳', over_7y: '🌲',
  not_met_yet: '📱', first_date_soon: '📅', few_dates_in: '✨', just_started_seeing: '💫',
  new_friend: '👋', few_years: '🫱', since_forever: '♾️',
  // connection_goal
  fun: '😂', closeness: '💞', understanding: '☕', growth: '🌊',
  // main_barrier
  questions: '❓', distraction: '📱', time_energy: '😴', repetition: '🔁', awkward_vulnerability: '😬', none: '✅',
  // last_real_conversation / first_date_worry / catchup_pattern
  this_week: '📆', this_month: '🗓️', cant_remember: '🌫️', we_talk_like_this_often: '💬',
  running_out_of_things: '🫠', feels_like_interview: '🎙️', being_too_much: '🌶️', nothing_really: '😌',
  same_topics: '🔁', we_go_deep: '🌊', depends: '🎲',
  // intentional_time_frequency
  daily: '📅', weekly: '🗓️', monthly: '🌙', rarely: '🌦️', first_time: '✨',
  // social_energy
  soft: '🌙', curious: '🤔', playful: '😜', bold: '🔥',
  // intended_setting
  home: '🏠', travelling_walking: '🚶', friends: '👯', later: '🔖',
  // available_time
  '5_10': '⏱️', '15_20': '⏳', '30_45': '🕰️', flexible: '🤸',
  // preferred_depth
  '1': '🌤️', '2': '🎈', '3': '🌊', '4': '⚡',
  // topic_boundaries
  exes: '💔', money: '💰', family: '👨‍👩‍👧', future: '🔮', sex_intimacy: '🌶️', religion: '🙏', politics: '🏛️', prefer_not_to_say: '🤐',
  // scan_trigger
  social_video: '📹', friend_told_me: '🗣️', card: '🃏', time_together: '⏰', reconnecting: '💞',
  repeated_conversation: '🔁', first_meeting: '👋', curiosity: '🤔',
  // return_value
  experiences: '🎉', guided_rituals: '🧭', memory_capsules: '📸', insights: '💡', unlimited_hosting: '♾️',
}
